import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Breadcrumbs,
    Link,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Home as HomeIcon,
    Settings as SettingsIcon,
    Forum as ForumIcon,
    Logout as LogoutIcon,
    Language as LanguageIcon,
    Collections as GalleryIcon,
    ViewCarousel as SliderIcon,
    Folder as FolderIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Bookmark as BookmarkIcon,
    DynamicFeed as MyThreadsPostsIcon,
    HelpOutline as HelpIcon,
    Public as PublicIcon,
    Menu as MenuIcon,
    Close as CloseIcon,
    Checklist,
    Inbox as InboxIcon,
    EditNote as EditNoteIcon,
    Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../utils/axiosConfig';
import NotificationBell from './NotificationBell';
import { brand } from '../theme';
import cffaLogo from '../assets/cffa-logo.png';

const drawerWidth = 240;

const Dashboard = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved !== null ? JSON.parse(saved) : true; // Default to collapsed
    });

    // Save sidebar state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    }, [sidebarCollapsed]);

    const menuItems = [
        { text: t('dashboard.home'), icon: <HomeIcon />, path: '/dashboard' },
        { text: t('dashboard.reminders'), icon: <Checklist />, path: '/dashboard/reminders' },
        { text: t('dashboard.forums'), icon: <ForumIcon />, path: '/dashboard/forums' },
        { text: t('dashboard.followedThreads'), icon: <BookmarkIcon />, path: '/dashboard/followed-threads' },
        { text: t('dashboard.myThreadsPosts'), icon: <MyThreadsPostsIcon />, path: '/dashboard/my-threads-posts' },
        { text: t('dashboard.resources'), icon: <FolderIcon />, path: '/dashboard/resources' },
        { text: t('dashboard.gallery'), icon: <GalleryIcon />, path: '/dashboard/gallery' },
    ];

    const adminMenuItems = [
        { text: 'Slideshow', icon: <SliderIcon />, path: '/dashboard/sliders' },
    ];

    const isAdmin = user && user.role && user.role.name.toLowerCase() === 'admin';
    const hasManageSettings = user?.role?.Permissions?.some(p => p.name === 'manage_settings');

    // Submissions: admins or roles granted the manage_submissions permission.
    const hasManageSubmissions = user?.role?.Permissions?.some(p => p.name === 'manage_submissions');
    if (isAdmin || hasManageSubmissions) {
        adminMenuItems.push({ text: t('dashboard.submissions'), icon: <InboxIcon />, path: '/dashboard/submissions' });
    }

    // Public site CMS: admins or roles granted the manage_public_content permission.
    const hasManagePublicContent = user?.role?.Permissions?.some(p => p.name === 'manage_public_content');
    const cmsMenuItems = (isAdmin || hasManagePublicContent) ? [
        { text: t('cms.menu.siteContent'), icon: <EditNoteIcon />, path: '/dashboard/site-content' },
        { text: t('cms.menu.locations'), icon: <StorefrontIcon />, path: '/dashboard/locations' },
    ] : [];

    const allMenuItems = [
        ...menuItems,
        ...(isAdmin || hasManageSettings ? adminMenuItems : []),
        ...cmsMenuItems
    ];

    const bottomMenuItems = [
        { text: t('dashboard.settings'), icon: <SettingsIcon />, path: '/dashboard/settings' },
        { text: t('dashboard.publicSite'), icon: <PublicIcon />, path: '/' },
    ];

    const handleLogout = () => {
        onLogout();
        navigate('/login');
    };

    const handleLanguageMenuOpen = (event) => {
        setLanguageMenuAnchor(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchor(null);
    };

    const handleLanguageChange = async (language) => {
        i18n.changeLanguage(language);
        handleLanguageMenuClose();

        // Save language preference to user profile
        try {
            await axios.patch(`/api/users/${user.id}`, { language });
        } catch (error) {
            console.error('Error saving language preference:', error);
        }
    };

    const isPathSelected = (path) => {
        if (path === '/dashboard') return location.pathname === '/dashboard';
        if (path === '/') return false;
        return location.pathname.startsWith(path);
    };

    const navButtonSx = (selected) => ({
        justifyContent: (sidebarCollapsed && !isMobile) ? 'center' : 'flex-start',
        mx: 0.75,
        my: 0.25,
        borderRadius: '10px',
        color: selected ? brand.orange400 : brand.cream100,
        backgroundColor: selected ? 'rgba(226, 87, 31, 0.22)' : 'transparent',
        '&:hover': {
            backgroundColor: selected ? 'rgba(226, 87, 31, 0.32)' : 'rgba(255, 250, 243, 0.08)',
        },
        '& .MuiListItemIcon-root': { color: 'inherit' },
    });

    const generateBreadcrumbs = () => {
        const pathnames = location.pathname.split('/').filter((x) => x);
        const breadcrumbMap = {
            'dashboard': t('dashboard.title'),
            'settings': t('settings.title'),
            'profile': t('profile.title'),
            'sessions': t('settings.sessions'),
            'admin': t('settings.adminSettings'),
            'users': t('settings.manageUsers'),
            'roles': t('settings.manageRoles'),
            'permissions': t('settings.managePermissions'),
            'forums': t('dashboard.forums'),
            'resources': t('dashboard.resources'),
            'gallery': t('dashboard.gallery'),
            'followed-threads': t('dashboard.followedThreads'),
            'my-threads-posts': t('dashboard.myThreadsPosts'),
            'help': t('dashboard.help'),
            'sliders': t('dashboard.sliders'),
            'reminders': t('dashboard.reminders'),
            'site-content': t('cms.menu.siteContent'),
            'locations': t('cms.menu.locations'),
        };

        return pathnames.slice(1).map((name, index) => {  // Skip 'dashboard' from breadcrumbs
            const to = `/${pathnames.slice(0, index + 2).join('/')}`;
            const isLast = index === pathnames.slice(1).length - 1;
            const displayName = breadcrumbMap[name] || name.charAt(0).toUpperCase() + name.slice(1);
            return isLast ? (
                <Typography key={to} color="text.primary">
                    {displayName}
                </Typography>
            ) : (
                <Link key={to} component={RouterLink} to={to} color="inherit">
                    {displayName}
                </Link>
            );
        });
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                            sx={{ mr: 2 }}
                        >
                            {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>
                    )}
                    <Box
                        component={RouterLink}
                        to="/dashboard"
                        sx={{
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            lineHeight: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={cffaLogo}
                            alt="CFFA Group"
                            sx={{
                                height: 34,
                                width: 'auto',
                                mr: 1.25,
                                flex: 'none',
                                objectFit: 'contain',
                                p: 0.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.7)',
                                boxShadow: '0 0 6px rgba(255, 255, 255, 0.6)',
                                cursor: 'pointer',
                            }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton color="inherit" onClick={() => navigate('/dashboard/help')} title={t('dashboard.help')} sx={{ mr: 0.5 }}>
                        <HelpIcon />
                    </IconButton>
                    <NotificationBell />
                    {/* Profile image and name as plain text, no link */}
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                        {user.displayPicture && (
                            <img
                                src={user.displayPicture.startsWith('/') ? user.displayPicture : `/uploads/images/${user.displayPicture}`}
                                alt="Profile"
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', marginRight: 8 }}
                            />
                        )}
                        {!user.displayPicture && (
                            <img
                                src={`https://static.vecteezy.com/system/resources/previews/052/755/981/non_2x/a-man-profile-avatar-icon-with-a-white-background-free-vector.jpg`}
                                alt="Profile"
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', marginRight: 8 }}
                            />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                        </Typography>
                    </Box>
                    <IconButton color="inherit" onClick={handleLogout}>
                        <LogoutIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileDrawerOpen : true}
                onClose={() => setMobileDrawerOpen(false)}
                sx={{
                    width: isMobile ? drawerWidth : (sidebarCollapsed ? 64 : drawerWidth),
                    flexShrink: 0,
                    transition: 'width 0.3s',
                    [`& .MuiDrawer-paper`]: {
                        width: isMobile ? drawerWidth : (sidebarCollapsed ? 64 : drawerWidth),
                        boxSizing: 'border-box',
                        transition: 'width 0.3s',
                        overflowX: 'hidden',
                        backgroundColor: brand.brown950,
                        color: brand.cream100,
                        borderRight: 'none',
                    },
                }}
            >
                <Toolbar />
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        <List>
                            {allMenuItems.map((item) => (
                                <ListItem key={item.text} disablePadding>
                                    <ListItemButton
                                        component={RouterLink}
                                        to={item.path}
                                        onClick={() => isMobile && setMobileDrawerOpen(false)}
                                        sx={navButtonSx(isPathSelected(item.path))}
                                        title={(sidebarCollapsed && !isMobile) ? item.text : ''}
                                    >
                                        <ListItemIcon sx={{ minWidth: (sidebarCollapsed && !isMobile) ? 'unset' : 56 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        {(!sidebarCollapsed || isMobile) && <ListItemText primary={item.text} />}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                    <Box sx={{ borderTop: 1, borderColor: 'rgba(255, 250, 243, 0.12)' }}>
                        <List>
                            {/* Language Switcher */}
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleLanguageMenuOpen}
                                    sx={navButtonSx(false)}
                                    title={(sidebarCollapsed && !isMobile) ? t('dashboard.language') : ''}
                                >
                                    <ListItemIcon sx={{ minWidth: (sidebarCollapsed && !isMobile) ? 'unset' : 56 }}>
                                        <LanguageIcon />
                                    </ListItemIcon>
                                    {(!sidebarCollapsed || isMobile) && <ListItemText primary={t('dashboard.language')} />}
                                </ListItemButton>
                            </ListItem>
                            {bottomMenuItems.map((item) => (
                                <ListItem key={item.text} disablePadding>
                                    <ListItemButton
                                        component={RouterLink}
                                        to={item.path}
                                        onClick={() => isMobile && setMobileDrawerOpen(false)}
                                        sx={navButtonSx(isPathSelected(item.path))}
                                        title={(sidebarCollapsed && !isMobile) ? item.text : ''}
                                    >
                                        <ListItemIcon sx={{ minWidth: (sidebarCollapsed && !isMobile) ? 'unset' : 56 }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        {(!sidebarCollapsed || isMobile) && <ListItemText primary={item.text} />}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        {/* Collapse Toggle Button - Desktop Only */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', p: 1 }}>
                                <IconButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)} size="small">
                                    {sidebarCollapsed ? <ChevronRightIcon sx={{ color: 'white' }} /> : <ChevronLeftIcon sx={{ color: 'white' }} />}
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Drawer>
            <Menu
                anchorEl={languageMenuAnchor}
                open={Boolean(languageMenuAnchor)}
                onClose={handleLanguageMenuClose}
            >
                <MenuItem onClick={() => handleLanguageChange('en')}>{t('common.english')}</MenuItem>
                <MenuItem onClick={() => handleLanguageChange('fr')}>{t('common.french')}</MenuItem>
            </Menu>
            {/* minWidth: 0 is the fix for wide content (tables, tab bars) getting silently
                clipped instead of scrolling: as a flex item this box otherwise refuses to
                shrink below its content's natural width (flexbox's default min-width: auto),
                which pushes the whole layout wider than the viewport — and since body has
                overflow-x: hidden (index.css), that excess just gets cut off with no way to
                reach it, instead of the intended child (e.g. TableContainer) scrolling. */}
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
                <Toolbar />
                {!location.pathname.includes('/forums') && (
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, display: { xs: 'none', sm: 'flex' } }}>
                        <Link component={RouterLink} to="/dashboard" color="inherit">
                            {t('dashboard.title')}
                        </Link>
                        {generateBreadcrumbs()}
                    </Breadcrumbs>
                )}
                <Outlet />
            </Box>
        </Box>
    );
};

export default Dashboard;